import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Loader2 } from "lucide-react";

import { useApp } from "@/context/AppContext";
import FixedHeader from "@/components/site/FixedHeader";
import Footer from "@/components/site/Footer";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 3I.4T (visual refresh) — Interest thank-you page.
 *
 * The interest thank-you page IS a page of orangebysite.com, so it MUST
 * reuse the canonical public-site shell — same <FixedHeader/> (TopPromo
 * + Nav with HOME / WEBSITES / PRODUCTS / PORTFOLIO / CONTACT US +
 * currency/language selectors + "Build my system" CTA + the canonical
 * WEBSITE logo asset), the exact orange radial-gradient canvas used by
 * PublicSite.jsx, and the same <Footer/> component. No standalone
 * white card, no email-logo reuse.
 *
 * Journey (unchanged):
 *   1. Recipient clicks "YEAH, I'M INTERESTED!" in email.
 *   2. /api/track/pitch/interest/{tok} logs the click and 302s here.
 *   3. This SPA auto-POSTs /api/pitches/interest/{tok}/confirm on mount,
 *      which proves JavaScript executed (email scanners typically don't
 *      run JS) — combined with the server-side scanner-UA short-list this
 *      is the industry-standard one-click mitigation.
 *   4. Server writes canonical Interested + interested_source=follow_up_email
 *      on BOTH the follow-up and the parent pitch. Idempotent.
 *   5. This page renders the branded thank-you inline on the orange canvas.
 *
 * Test-mode isolation preserved:
 *   - token === "test-disabled" → no confirm POST fires, no analytics,
 *     small "TEST MODE" badge + note, but the same branded shell renders
 *     so the operator sees exactly what the prospect will see.
 *   - token === "preview" → developer preview only.
 */
export default function InterestConfirmPage() {
  const { token } = useParams();
  const { dir } = useApp();
  const [state, setState] = useState("verifying"); // verifying | confirmed | already | test | preview | error
  const [error, setError] = useState(null);
  const [prospect, setProspect] = useState(null);

  useEffect(() => {
    if (!token) return;
    if (token === "preview") { setState("preview"); return; }
    if (token === "test-disabled") { setState("test"); return; }

    let cancelled = false;
    (async () => {
      try {
        const ctx = await axios.get(`${API}/api/pitches/interest/${token}`);
        if (cancelled) return;
        setProspect(ctx.data);
        if (ctx.data.already_confirmed) { setState("already"); return; }
        await axios.post(`${API}/api/pitches/interest/${token}/confirm`, {});
        if (cancelled) return;
        setState("confirmed");
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || "This link is invalid or expired.");
        setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const isTestPreview = state === "test";
  const isPreview = state === "preview";
  const showThankYou = state === "confirmed" || state === "already" || isTestPreview || isPreview;

  return (
    <div
      className="App relative text-black min-h-screen flex flex-col"
      dir={dir}
      data-testid="interest-confirm-page"
    >
      {/* Exact orange canvas + noise overlay copied from PublicSite.jsx */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 90% 80% at 58% 45%, #FFB347 0%, #FF8A2E 30%, #FF7A00 55%, #FF6A00 80%, #D94C00 100%)",
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 120% 100% at 50% 50%, transparent 55%, rgba(90,25,0,0.28) 100%)",
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 -z-10 pointer-events-none opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          backgroundSize: "180px 180px",
        }}
      />

      {/* Canonical live-site header (TopPromo + Nav w/ real logo) */}
      <FixedHeader />

      {/* Main hero sits directly on the orange canvas — no floating card */}
      <main className="relative pt-[204px] lg:pt-[132px] flex-1">
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-20 pb-20 md:pb-28">
          <div className="max-w-3xl">
            {(state === "verifying") && (
              <div
                className="inline-flex items-center gap-2 text-black/70 font-semibold text-[13px] tracking-wide"
                data-testid="interest-verifying"
              >
                <Loader2 size={16} className="animate-spin" />
                Recording your interest…
              </div>
            )}

            {isTestPreview && (
              <div
                className="inline-flex items-center h-7 px-3 rounded-full bg-black text-white text-[10px] tracking-[0.28em] uppercase font-black mb-8"
                data-testid="interest-test-mode-badge"
              >
                Test mode
              </div>
            )}

            {isPreview && (
              <div
                className="inline-flex items-center h-7 px-3 rounded-full bg-black text-white text-[10px] tracking-[0.28em] uppercase font-black mb-8"
                data-testid="interest-preview-mode-badge"
              >
                Preview
              </div>
            )}

            {showThankYou && (
              <>
                <div
                  className="text-[11px] md:text-[12px] tracking-[0.32em] uppercase font-black text-black/80"
                  data-testid="interest-thankyou-eyebrow"
                >
                  Thank you for your interest
                </div>

                <h1
                  className="font-display font-black text-black leading-[0.92] tracking-[-0.03em] mt-5 md:mt-6"
                  style={{ fontSize: "clamp(2.6rem, 8vw, 5.75rem)" }}
                  data-testid="interest-thankyou-title"
                >
                  Great — let's talk.
                </h1>

                <p
                  className="mt-8 md:mt-10 text-black/85 font-medium max-w-2xl leading-[1.55]"
                  style={{ fontSize: "clamp(1rem, 1.6vw, 1.2rem)" }}
                  data-testid="interest-thankyou-body"
                >
                  Thanks for letting us know you're interested.
                  <br />
                  <br />
                  We'd love to learn a little more about your business and show
                  you how we could build a system around the way your company
                  actually works.
                  <br />
                  <br />
                  We'll be in touch to arrange a call or Zoom meeting to discuss
                  your requirements and explore what we could build for you.
                </p>

                <p
                  className="mt-10 md:mt-14 font-display font-black uppercase text-black tracking-[0.32em] text-[12px] md:text-[13px]"
                  data-testid="interest-thankyou-closing"
                >
                  Your business. One system.
                </p>

                <div className="mt-8 md:mt-10">
                  <a
                    href="/"
                    data-testid="interest-explore-cta"
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-full border-2 border-black text-black text-[11px] font-bold uppercase tracking-[0.22em] hover:bg-black hover:text-white transition-colors"
                  >
                    Explore OrangeBySite
                  </a>
                </div>

                {isTestPreview && (
                  <p
                    className="mt-8 text-black/70 text-[12px] max-w-lg leading-relaxed"
                    data-testid="interest-test-mode-note"
                  >
                    Test preview — no interest has been recorded and the
                    prospect record remains unchanged.
                  </p>
                )}

                {isPreview && (
                  <p
                    className="mt-8 text-black/70 text-[12px] max-w-lg leading-relaxed"
                    data-testid="interest-preview-mode-note"
                  >
                    Developer preview — send a real follow-up to enable the live
                    one-click confirmation flow.
                  </p>
                )}
              </>
            )}

            {state === "error" && (
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 text-black">
                  <AlertTriangle size={22} strokeWidth={2.4} />
                  <span className="text-[11px] tracking-[0.28em] uppercase font-black">
                    Link problem
                  </span>
                </div>
                <h1
                  className="font-display font-black text-black leading-[0.95] tracking-[-0.03em] mt-4"
                  style={{ fontSize: "clamp(2rem, 6vw, 3.75rem)" }}
                  data-testid="interest-error-title"
                >
                  We couldn't verify this link.
                </h1>
                <p className="mt-5 text-black/85 text-[15px] max-w-lg leading-relaxed">
                  {error}
                </p>
                <div className="mt-8">
                  <a
                    href="/"
                    data-testid="interest-error-home-cta"
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-full border-2 border-black text-black text-[11px] font-bold uppercase tracking-[0.22em] hover:bg-black hover:text-white transition-colors"
                  >
                    Go to OrangeBySite
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Canonical live-site footer (reused component — inherits changes) */}
      <Footer />
    </div>
  );
}
